import { useState } from "react";
import { FileSearch, NotepadText, Watch } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Assessment } from "@/lib/api_client";

/**
 * Audit section shown once per analysis, or as a bare "Vis AI-input" per
 * variant when the variants' snapshots differ (retrieved examples):
 *
 * - Alert banner when consultation notes exist — reminds the reviewer to
 *   verify that key points from the notes are reflected in the analysis.
 * - Alert banner when wearable data was included — shows the now-slice
 *   coverage so a doctor can immediately see when the wearable signal is
 *   too sparse to fully trust.
 * - "Vis AI-input" button — opens a modal showing the exact prompt text the
 *   AI received (snapshotted at generation time on `assessment.aiInputSnapshot`).
 */
export function AssessmentAuditButtons({
  assessment,
  hasConsultationNotes,
  hasWearableData = false,
  wearableCoverageLabel,
  variantLabel,
}: {
  assessment: Assessment | null;
  hasConsultationNotes: boolean;
  /** True when the assessment input included a [Helsedata fra wearables] section. */
  hasWearableData?: boolean;
  /** Source + coverage summary, e.g. "Garmin, 27/30 dager". Shown next to the wearable banner. */
  wearableCoverageLabel?: string;
  variantLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const snapshot = assessment?.aiInputSnapshot ?? null;

  if (!hasConsultationNotes && !hasWearableData && !snapshot) return null;

  return (
    <div className="space-y-2">
      {hasConsultationNotes && (
        <div className="flex items-start gap-2 rounded border border-admin-warn-500/30 bg-admin-warn-500/10 p-2.5">
          <NotepadText className="h-4 w-4 text-admin-warn-400 mt-0.5 shrink-0" />
          <p className="text-xs text-admin-warn-200/90 leading-relaxed">
            Det ble tatt notater i forbindelse med konsultasjon under denne
            prøvetakingen. Verifiser at eventuelle viktige funn fra notatene er
            tilstrekkelig og på riktig vis inkludert i analysen.
          </p>
        </div>
      )}
      {hasWearableData && (
        <div className="flex items-start gap-2 rounded border border-admin-sky-500/30 bg-admin-sky-500/10 p-2.5">
          <Watch className="h-4 w-4 text-admin-sky-400 mt-0.5 shrink-0" />
          <p className="text-xs text-admin-sky-200/90 leading-relaxed">
            Inkl. wearable-data
            {wearableCoverageLabel ? ` (${wearableCoverageLabel})` : ""}.
            Verifiser at eventuelle wearable-baserte påstander i analysen
            stemmer med tabellen under, og vær særlig kritisk når dekningen
            er lav.
          </p>
        </div>
      )}
      {snapshot && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-[10px] text-admin-text-subtle hover:text-admin-text-primary"
          onClick={() => setOpen(true)}
          title="Vis nøyaktig tekst sendt til AI-en"
        >
          <FileSearch className="h-3 w-3 mr-1" />
          Vis AI-input
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>AI-input — {variantLabel}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-admin-text-subtle">
            Dette er den eksakte teksten som ble sendt til AI-en da denne
            analysen ble generert. Bruk dette til å verifisere at f.eks.
            konsultasjonsnotater faktisk var med i inputen.
          </p>
          <pre className="flex-1 overflow-auto text-xs bg-admin-card/40 border border-admin-card-border rounded p-3 whitespace-pre-wrap font-mono">
            {snapshot}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
